// Quo messaging — "Call" from a member thread.
//
// Quo's public API cannot place a call, so the button hands the call to the
// Quo app on this device after a confirm step that shows the Quo number the
// call will come FROM:
//   - mobile (iOS/Android): Quo's documented deep link
//       openphone://dial?number=<member>&from=<quo>&action=call
//     which auto-dials (App Store / Play Store redirect if not installed);
//   - desktop: a tel: link, which the Quo desktop app answers once it is the
//     default calling app (Quo → click-to-call setup; linked in the dialog).
// The call itself, and its AI summary on Business/Scale plans, land back on
// this thread through the call.* webhooks — see docs/quo-messaging.md.
//
// Rendered only when the account's provider is Quo with a live inbox and the
// member has a phone number; works for direct AND PadSplit members.

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ExternalLink, Phone, PhoneOutgoing } from "lucide-react";

type ProviderState = {
  provider: "none" | "twilio" | "quo";
  quo: { status: string; phoneNumberId: string | null; phoneNumber: string | null; phoneNumberName: string | null } | null;
};

const QUO_CLICK_TO_CALL_HELP = "https://support.quo.com/core-concepts/calling/click-to-call";

export function normalizeDialNumber(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith("+")) {
    const digits = trimmed.slice(1).replace(/\D/g, "");
    return digits.length >= 6 ? `+${digits}` : null;
  }
  const digits = trimmed.replace(/\D/g, "");
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return digits.length >= 6 ? `+${digits}` : null;
}

export function isMobileUserAgent(ua: string): boolean {
  return /Android|iPhone|iPad|iPod/i.test(ua);
}

/** The href the Call button navigates to. Exported for tests. */
export function buildQuoDialHref(args: { memberPhone: string; quoPhone: string; mobile: boolean }): string {
  if (args.mobile) {
    return `openphone://dial?number=${encodeURIComponent(args.memberPhone)}&from=${encodeURIComponent(args.quoPhone)}&action=call`;
  }
  return `tel:${args.memberPhone}`;
}

export function QuoCallButton({
  memberId,
  memberName,
  compact = false,
}: {
  memberId: number;
  memberName: string | null;
  compact?: boolean;
}) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);

  // 404 when the quo_messaging beta isn't granted → treat as "no Quo".
  const providerQuery = useQuery<ProviderState>({ queryKey: ["/api/app/messaging-provider"], retry: false });
  const memberQuery = useQuery<{ phone?: string | null; name?: string | null }>({ queryKey: ["/api/app/members", memberId] });

  const quo = providerQuery.data?.provider === "quo" && providerQuery.data.quo?.status === "active" ? providerQuery.data.quo : null;
  const memberPhone = memberQuery.data?.phone ? normalizeDialNumber(memberQuery.data.phone) : null;
  if (!quo?.phoneNumber || !memberPhone) return null;

  const displayName = memberName || memberQuery.data?.name || "this member";
  const mobile = typeof navigator !== "undefined" && isMobileUserAgent(navigator.userAgent);
  const fromLabel = `${quo.phoneNumberName ? `${quo.phoneNumberName} · ` : ""}${quo.phoneNumber}`;

  const placeCall = () => {
    const href = buildQuoDialHref({ memberPhone, quoPhone: quo.phoneNumber!, mobile });
    setOpen(false);
    toast({
      title: mobile ? "Opening Quo…" : "Handing the call to Quo…",
      description: `Calling ${displayName} from ${fromLabel}. The call and its summary will appear in this conversation when it ends.`,
    });
    window.location.assign(href);
  };

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant={compact ? "outline" : "ghost"}
        className={compact ? "h-7 px-2 text-xs" : "h-8 px-2 text-xs text-gray-600"}
        onClick={() => setOpen(true)}
        data-testid="button-quo-call"
        title={`Call ${displayName} from Quo (${quo.phoneNumber})`}
      >
        <Phone className="h-4 w-4 sm:mr-1" />
        <span className={compact ? "" : "hidden sm:inline"}>Call</span>
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="dialog-quo-call">
          <DialogHeader>
            <DialogTitle>Confirm calling from</DialogTitle>
            <DialogDescription>
              This call is placed through Quo, so {displayName} sees your Quo number, not your personal one.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
              <div className="text-xs uppercase tracking-wide text-gray-500">Calling from</div>
              <div className="mt-0.5 text-base font-semibold text-gray-900" data-testid="text-quo-call-from">
                {fromLabel}
              </div>
            </div>
            <div className="rounded-md border border-gray-200 p-3">
              <div className="text-xs uppercase tracking-wide text-gray-500">Calling</div>
              <div className="mt-0.5 font-medium text-gray-900" data-testid="text-quo-call-to">
                {displayName} · {memberPhone}
              </div>
            </div>
            <p className="text-xs text-gray-600" data-testid="text-quo-call-hint">
              {mobile
                ? "The Quo app opens and dials automatically. When the call ends, it appears in this conversation with its summary (Business and Scale plans)."
                : "Quo opens and dials if it is your default calling app. When the call ends, it appears in this conversation with its summary (Business and Scale plans)."}
              {!mobile && (
                <>
                  {" "}
                  <a
                    href={QUO_CLICK_TO_CALL_HELP}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-0.5 text-brand underline"
                    data-testid="link-quo-click-to-call-help"
                  >
                    Set Quo as your default calling app <ExternalLink className="h-3 w-3" />
                  </a>
                </>
              )}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="button-cancel-quo-call">
              Cancel
            </Button>
            <Button onClick={placeCall} data-testid="button-confirm-quo-call">
              <PhoneOutgoing className="h-4 w-4 mr-2" />
              Call
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
